import { Injectable } from '@angular/core';
import { IOrder } from './models/order.model';
import { IProductSession } from '@core/product/models/product-session.model';
import { ProductHelperService } from '@core/product/product-helper.service';
import { IOrderItemSelection } from '@core/checkout/model/checkout-order-item-selection.model';
import { IProductPackageItem } from '@core/product/models/product-package-item.model';
import { IOrderItem } from '@core/order/models/order-item.model';
import { INumberIncrementProductItem } from '@core/form/number-increment/number-increment-product-item.model';
import { ProductListService } from '@core/product/product-list.service';
import { OrderProductOptionSetService } from './order-product-option-set.service';

@Injectable({
  providedIn: 'root'
})
export class OrderPartyPackageService {
  constructor(
    private productHelperService: ProductHelperService,
    private productListService: ProductListService,
    private orderProductOptionSetService: OrderProductOptionSetService
  ) {}

  /**
   * Determine how many quantities the guest needs to select a party package item based on the order item quantity
   * Only applied to party package items that are 'guest' quantity type
   * @param packageItem party package item reference
   * @param orderItem matching basket item for the party package item
   * @returns amount of guest selections
   */
  getRequiredOrderSelectionQuantity(packageItem: IProductPackageItem, orderItem?: IOrderItem): number {
    if (packageItem.quantityType === 'guest') {
      const packageQuantity = ((orderItem?.quantity || 0) / packageItem.quantityPerGuest) * packageItem.quantity;

      return packageItem.packageRequirement === 'after' ? Math.floor(packageQuantity) : Math.ceil(packageQuantity);
    } else {
      return packageItem.quantity;
    }
  }

  onOrderItemSelection(
    order: IOrder,
    ev: INumberIncrementProductItem,
    packageItem: IProductPackageItem,
    session?: IProductSession
  ): undefined | { item: IOrderItem | undefined; isAddition: boolean } {
    if (!session) {
      return;
    }

    const partyItem = order.items
      .filter((i) => i.productType === 'partypackage')
      .find(
        (item) =>
          item.productId === packageItem.packageProductId &&
          this.productHelperService.isSameSession(item.session, session)
      );
    if (!partyItem) {
      return;
    }

    // Get the matching selection for the package item index. Filter only by parent product id when single selection to avoid adding multiple selections
    const existingSelectionIndex = partyItem.orderSelections?.findIndex((selection: IOrderItemSelection) => {
      if (packageItem.isSingleSelection) {
        return selection.parentProductId === packageItem.parentProductId;
      }

      return selection.productId === ev.id && selection.parentProductId === packageItem.parentProductId;
    });

    const exists = existingSelectionIndex !== -1;
    const isAddition = !exists ? true : partyItem.orderSelections[existingSelectionIndex].quantity < ev.quantity;

    if (exists && ev.quantity === 0) {
      partyItem.orderSelections.splice(existingSelectionIndex, 1);
    } else {
      const selection: IOrderItemSelection = {
        parentProductId: packageItem.parentProductId,
        productId: ev.product.id,
        quantity: ev.quantity,
        parentProductName: packageItem.name,
        productName: ev.product.name
      };

      exists
        ? (partyItem.orderSelections[existingSelectionIndex] = selection)
        : partyItem.orderSelections.push(selection);
    }

    return { item: partyItem, isAddition };
  }

  /**
   * This handles the situations where the guest hasn't met the party package order selection requirements, but the item is already in cart.
   * In this case, we need to update the existing selections to meet the required amounts, in some cases adding the first selection if one was missed
   */
  setRequiredSelection(packageItems: IProductPackageItem[], orderItem: IOrderItem): IOrderItem {
    packageItems?.forEach((item: IProductPackageItem) => {
      // Get all the selections for the package item variations
      const basketSelectionVariations = orderItem?.orderSelections?.filter((selection: IOrderItemSelection) => {
        return selection.parentProductId === item.parentProductId;
      });

      // Sum the total of all selected variation on the package order item
      const variationsTotal = basketSelectionVariations?.reduce(
        (total, selection: IOrderItemSelection) => total + selection.quantity,
        0
      );

      // Current required selection amount is the default minus amount already selected
      const remainingRequiredSelectionAmount =
        this.getRequiredOrderSelectionQuantity(item, orderItem) - (variationsTotal || 0);

      // No further selections required
      if (!remainingRequiredSelectionAmount || remainingRequiredSelectionAmount === 0) {
        return;
      }

      if (basketSelectionVariations?.length) {
        if (remainingRequiredSelectionAmount > 0) {
          // update first matching variation quantity to include the remaining required amount
          basketSelectionVariations[0].quantity += remainingRequiredSelectionAmount;
        } else {
          // removes the required number of items starting from the last one added to the cart
          let remainingToRemove = Math.abs(remainingRequiredSelectionAmount);
          basketSelectionVariations.reverse().forEach((selection: IOrderItemSelection) => {
            if (remainingToRemove === 0 || selection.quantity === 0) {
              return;
            }

            if (selection.quantity <= remainingToRemove) {
              // If this variation has fewer items than we need to remove, remove all of them
              remainingToRemove -= selection.quantity;
              selection.quantity = 0;
            } else {
              // Otherwise, remove just what we need
              selection.quantity -= remainingToRemove;
              remainingToRemove = 0;
            }
          });

          // Remove any selections with quantity 0 once done
          orderItem.orderSelections = orderItem.orderSelections.filter((selection) => selection.quantity > 0);
        }
      } else {
        // Add the first variation with the required selection
        orderItem.orderSelections.push({
          parentProductId: item.variations[0].parentProductId || '',
          productId: item.variations[0].id,
          quantity: remainingRequiredSelectionAmount,
          productName: item.variations[0].name,
          parentProductName: item.name
        });
      }
    });

    return orderItem;
  }

  getPartyPackageItemsForCalcs(partyPackageOrderItems: IOrderItem[]): IOrderItem[] {
    const items: IOrderItem[] = [];
    partyPackageOrderItems.forEach((partyPackageOrderItem: IOrderItem) => {
      const baseItem = structuredClone(partyPackageOrderItem);
      const mainTicketCost = this.calculatePartyPackageMainTicketCost(partyPackageOrderItem);
      items.push({
        ...baseItem,
        cost: mainTicketCost
      });

      this.addPartyPackageItemsForCalcs(items, baseItem);
      this.addPartyPackageOptionSetsForCalcs(items, baseItem);
    });

    return items;
  }

  private addPartyPackageItemsForCalcs(items: IOrderItem[], partyPackageOrderItem: IOrderItem) {
    const packageItems = partyPackageOrderItem.product.packageItems || [];
    const packageIncludedItems = partyPackageOrderItem.product.packageIncludedItems || [];
    const allPackageItems = [...packageItems, ...packageIncludedItems];
    allPackageItems.forEach((packageItem: IProductPackageItem) => {
      const parentProduct = this.productListService.getParentProductLookup().get(packageItem.parentProductId);
      if (!parentProduct?.products?.length) {
        return;
      }

      const isGuestSelection = packageItem.productId === packageItem.parentProductId;
      const productId = isGuestSelection ? parentProduct.products[0].id : packageItem.productId;
      const product = this.productListService.getTicketsLookup().get(productId);
      if (!product) {
        return;
      }

      items.push({
        ...partyPackageOrderItem,
        quantity: this.getRequiredOrderSelectionQuantity(packageItem, partyPackageOrderItem),
        cost: packageItem.cost,
        tax: product.tax,
        isTaxInclusive: product.isTaxInclusive,
        perProductBookingFee: product.perProductBookingFee
      });
    });
  }

  private addPartyPackageOptionSetsForCalcs(items: IOrderItem[], partyPackageOrderItem: IOrderItem) {
    partyPackageOrderItem.product.packageOptionSets?.forEach((optionSet) => {
      items.push({
        ...partyPackageOrderItem,
        quantity: this.orderProductOptionSetService.getRequiredOrderSelectionQuantity(optionSet, partyPackageOrderItem),
        cost: optionSet.cost,
        tax: partyPackageOrderItem.product.tax,
        isTaxInclusive: partyPackageOrderItem.product.isTaxInclusive,
        perProductBookingFee: partyPackageOrderItem.product.perProductBookingFee
      });
    });
  }

  private calculatePartyPackageMainTicketCost(partyPackageOrderItem: IOrderItem) {
    const totalCost = partyPackageOrderItem.cost * partyPackageOrderItem.quantity;
    const inclusionsCost =
      this.calculatePackageItemsTotalCost(partyPackageOrderItem) +
      this.orderProductOptionSetService.calculateOptionSetsTotalCost(partyPackageOrderItem);

    return (totalCost - inclusionsCost) / partyPackageOrderItem.quantity;
  }

  private calculatePackageItemsTotalCost(partyPackageOrderItem: IOrderItem) {
    const packageItems = partyPackageOrderItem.product.packageItems || [];
    const packageIncludedItems = partyPackageOrderItem.product.packageIncludedItems || [];
    const allPackageItems = [...packageItems, ...packageIncludedItems];

    return (
      allPackageItems.reduce((total, packageItem) => {
        const quantity = this.getRequiredOrderSelectionQuantity(packageItem, partyPackageOrderItem);
        return total + packageItem.cost * quantity;
      }, 0) || 0
    );
  }
}
